import { Button, Popover, Slider } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { NEAR_RADIUS_STOPS_MI } from "../redux/global-map.constants.ts"
import { selectActiveFilters, setFilters } from "../redux/global-map.slice"

const ZIP_RE = /^\d{5}$/

// How long a slider drag pauses before the value actually reaches the
// store (and so the real fetch) — the slider's own visual handle still
// moves instantly on every mark it crosses; only the network-triggering
// dispatch is debounced. Matches this feature's other "live but not
// request-per-tick" precedent (`VIEWPORT_DEBOUNCE_MS`).
const RADIUS_COMMIT_DEBOUNCE_MS = 200

// Index 2 (5 mi) is a reasonable "haven't picked yet" starting point — a
// bare index 0 (0.5 mi) would be a near-useless default radius to open the
// popover on, and 0/undefined both mean "no prior choice" the same way.
const DEFAULT_RADIUS_INDEX = 2

function radiusToIndex(radius: number | null | undefined): number {
  if (radius == null) return DEFAULT_RADIUS_INDEX
  const index = (NEAR_RADIUS_STOPS_MI as readonly number[]).indexOf(radius)
  return index === -1 ? DEFAULT_RADIUS_INDEX : index
}

const SLIDER_MARKS = Object.fromEntries(
  NEAR_RADIUS_STOPS_MI.map((_, index) => [index, " "])
)

/**
 * "Look Around a Zip Code" — rendered by `GlobalMapFilterBar`, immediately
 * left of "Clear All" (both tabs), per explicit product direction. Moved
 * here from an earlier spot beside the Map/List/Analytics tabs
 * (`GlobalMapToolbar`'s row 1) — a real narrowing filter like Status or
 * Region belongs beside Clear All, not beside the tab list.
 *
 * One `Popover` wraps both possible children (the plain trigger pill, or
 * the applied zip+slider control) so the SAME popover instance opens from
 * either entry point — `trigger={[]}` disables antd's own click-to-open
 * (which would otherwise also fire from clicking the applied view's slider
 * or clear button, since they live inside the same wrapped child); opening
 * is instead driven explicitly by `openPopover`, called from the collapsed
 * trigger's onClick and the applied view's zip-label onClick alike. Closing
 * on an outside click still works via the controlled `open`/`onOpenChange`
 * pair, which antd honors regardless of `trigger`.
 *
 * Three states, matching the approved mockup exactly:
 * 1. Collapsed — `filters.nearZip` unset. A plain trigger pill; clicking it
 *    opens the popover asking for the zip code and a starting radius (the
 *    six fixed stops as one-tap marks via `Slider`'s own `marks`+
 *    `step={null}`, which snaps to the nearest mark rather than
 *    free-dragging between them). Apply commits both fields together — the
 *    map never re-centers on a half-typed zip — Cancel discards the draft.
 * 2. Applied — the trigger itself becomes a live slider: the zip stays
 *    labelled, dragging the radius handle re-commits `nearRadiusMi` on a
 *    short debounce (`RADIUS_COMMIT_DEBOUNCE_MS`) without reopening
 *    anything, and a trailing "✕" clears the whole filter back to state 1.
 *    Clicking the zip label reopens the same zip+radius popover from state
 *    1, pre-filled, to change the zip itself.
 *
 * Known, deliberate gap: a zip that is well-formed (5 digits) but cannot be
 * geocoded surfaces only as this page's existing generic fetch-error state
 * (`MapStates`'s fatal-error Alert), not a dedicated inline "we couldn't
 * find that zip" message under the input — `toGlobalMapError` has no
 * `field_errors`-reading path yet. Client-side format validation (5 digits)
 * catches the overwhelming majority of bad input before it ever reaches the
 * network; a bespoke inline error is a reasonable follow-up, not built here.
 */
export function LookAroundControl() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)

  const isApplied = filters.nearZip != null && filters.nearRadiusMi != null

  const [open, setOpen] = useState(false)
  const [draftZip, setDraftZip] = useState("")
  const [draftRadiusIndex, setDraftRadiusIndex] = useState(DEFAULT_RADIUS_INDEX)
  const [zipError, setZipError] = useState<string | null>(null)

  const openPopover = () => {
    setDraftZip(filters.nearZip ?? "")
    setDraftRadiusIndex(radiusToIndex(filters.nearRadiusMi))
    setZipError(null)
    setOpen(true)
  }

  const handleApply = () => {
    if (!ZIP_RE.test(draftZip)) {
      setZipError(t("globalMap.filters.lookAroundInvalidZip"))
      return
    }
    dispatch(
      setFilters({
        nearZip: draftZip,
        nearRadiusMi: NEAR_RADIUS_STOPS_MI[draftRadiusIndex]
      })
    )
    setOpen(false)
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    dispatch(setFilters({ nearZip: null, nearRadiusMi: null }))
  }

  // Live slider (applied state only) — visual position updates every mark
  // crossed; the actual store commit (and so the real fetch) is debounced.
  const [liveRadiusIndex, setLiveRadiusIndex] = useState(
    radiusToIndex(filters.nearRadiusMi)
  )
  const commitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => {
    setLiveRadiusIndex(radiusToIndex(filters.nearRadiusMi))
  }, [filters.nearRadiusMi])

  useEffect(() => () => clearTimeout(commitTimer.current), [])

  const handleLiveRadiusChange = (index: number) => {
    setLiveRadiusIndex(index)
    clearTimeout(commitTimer.current)
    commitTimer.current = setTimeout(() => {
      dispatch(setFilters({ nearRadiusMi: NEAR_RADIUS_STOPS_MI[index] }))
    }, RADIUS_COMMIT_DEBOUNCE_MS)
  }

  return (
    <Popover
      trigger={[]}
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => setOpen(next)}
      content={
        <div className="w-[260px]" data-testid="global-map-look-around-popover">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t("globalMap.filters.lookAroundTitle")}
          </div>
          <TextInput
            disableAutoCapitalize
            value={draftZip}
            onChange={(event) => {
              setDraftZip(event.target.value.replace(/[^\d]/g, "").slice(0, 5))
              setZipError(null)
            }}
            onPressEnter={handleApply}
            placeholder={t("globalMap.filters.lookAroundZipPlaceholder")}
            status={zipError ? "error" : undefined}
            aria-label={t("globalMap.filters.lookAroundZipPlaceholder")}
            className="mb-1"
          />
          {zipError && (
            <div className="mb-2 text-xs text-red-500">{zipError}</div>
          )}
          <div className="mb-1 mt-3 flex items-center justify-between text-xs text-slate-500">
            <span>{t("globalMap.filters.lookAroundRadiusLabel")}</span>
            <span className="font-semibold text-slate-800">
              {t("globalMap.filters.lookAroundMilesValue", {
                miles: NEAR_RADIUS_STOPS_MI[draftRadiusIndex]
              })}
            </span>
          </div>
          <Slider
            value={draftRadiusIndex}
            min={0}
            max={NEAR_RADIUS_STOPS_MI.length - 1}
            step={null}
            marks={SLIDER_MARKS}
            onChange={setDraftRadiusIndex}
            ariaLabelForHandle={t("globalMap.filters.lookAroundRadiusLabel")}
            className="mb-4"
          />
          <div className="flex gap-2">
            <Button onClick={() => setOpen(false)} className="flex-1">
              {t("globalMap.filters.lookAroundCancel")}
            </Button>
            <Button type="primary" onClick={handleApply} className="flex-1">
              {t("globalMap.filters.lookAroundApply")}
            </Button>
          </div>
        </div>
      }
    >
      {isApplied ? (
        <div
          className="flex h-9 shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white pl-3 pr-2 text-xs"
          data-testid="global-map-look-around-applied"
        >
          <span aria-hidden>📍</span>
          <button
            type="button"
            onClick={openPopover}
            className="font-medium text-slate-800 hover:underline"
            aria-label={t("globalMap.filters.lookAroundEditZip", {
              zip: filters.nearZip
            })}
          >
            {filters.nearZip}
          </button>
          <div className="w-[110px]">
            <Slider
              value={liveRadiusIndex}
              min={0}
              max={NEAR_RADIUS_STOPS_MI.length - 1}
              step={null}
              marks={SLIDER_MARKS}
              tooltip={{
                formatter: (index) =>
                  t("globalMap.filters.lookAroundMilesValue", {
                    miles: NEAR_RADIUS_STOPS_MI[index ?? 0]
                  })
              }}
              onChange={handleLiveRadiusChange}
              ariaLabelForHandle={t("globalMap.filters.lookAroundRadiusLabel")}
            />
          </div>
          <span className="whitespace-nowrap font-semibold text-slate-800">
            {t("globalMap.filters.lookAroundMilesValue", {
              miles: NEAR_RADIUS_STOPS_MI[liveRadiusIndex]
            })}
          </span>
          <button
            type="button"
            onClick={handleClear}
            aria-label={t("globalMap.filters.removeFilter", {
              filter: t("globalMap.filters.lookAround")
            })}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPopover}
          data-testid="global-map-look-around-trigger"
          className={cn(
            "flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
            "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          )}
        >
          <span aria-hidden>📍</span>
          {t("globalMap.filters.lookAround")}
          <span aria-hidden className="text-[10px] opacity-60">
            ▾
          </span>
        </button>
      )}
    </Popover>
  )
}

export default LookAroundControl
